/* eslint-disable consistent-return */
/* eslint-disable max-len */

import { FC, useCallback, useEffect, useState } from 'react';
import { Card, message, Modal, Result, Skeleton } from 'antd';
import { CheckCircleFilled, FireTwoTone } from '@ant-design/icons';
import { convertFormModelToFormBuilderForm } from 'modules/forms/utils';
import { convertMinsToHrsMins, localRandomUuid } from 'utils/string.utils';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import useWebSocket from 'utils/useWebSocket';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import { FormBuilderData } from 'types/formBuilder';
import cloneDeep from 'lodash/cloneDeep';
import RequestUpdateDraft from './RequestUpdateDraft';
import RequestUpdateNoDraft from './RequestUpdateNoDraft';
import { RequestFormProps } from '../RequestForm';

export const parseFormBuilder = (data: any): FormBuilderData => {
  const { description } = data;
  let questions: Record<string, any>[] = [];

  try {
    const desJson = typeof description === 'string' ? JSON.parse(description) : description;

    if (typeof desJson === 'object') {
      if (!Array.isArray(desJson)) {
        return desJson;
      }

      questions = desJson;
    }
  } catch (error) {
    questions = [
      {
        id: 'text',
        key: null,
        type: 'TextEditor',
        label: 'Description',
        answer: description,
        formType: 'Input',
        required: true,
        isQuestion: true,
      },
    ];
  }

  return convertFormModelToFormBuilderForm({
    uuid: data.uuid,
    title: data.title,
    description: data.description,
    questions,
  });
};

const parseData = (data: Record<string, any>) => {
  data.estimated_time_range = `${convertMinsToHrsMins(data.estimated_min)} - ${convertMinsToHrsMins(data.estimated_max)}`;

  return data;
};

export const fetchRequest = async (uuid: string, params?: Record<string, any>) => {
  const response = await apiRequests.get(`${apiRoutes.REQUESTS}/${uuid}`, params);

  return parseData(response.data.data);
};

export function isEstimatedMaxOverBudget(values: any, request: any): boolean {
  if (!('estimated_max' in values)) return false;

  const timeLeft: number = request?.project?.time_report?.available ?? 0;
  const currentEstimatedMax: number = request?.estimated_max ?? 0;
  const timeSpent: number = request?.time_spent ?? 0;
  const newEstimatedMax: number = values.estimated_max;

  if (timeSpent >= newEstimatedMax) return false;

  const budgetBaseline = Math.max(currentEstimatedMax, timeSpent);
  const additionalBudgetRequested = newEstimatedMax - budgetBaseline;

  if (additionalBudgetRequested <= 0) return false;

  if (additionalBudgetRequested <= timeLeft) return false;

  return true;
}

interface RequestUpdateProps {
  uuid: string;
  projectUuid?: string;
  pageUrl: string;
  wrapSize: 'xl' | 'xxl';
  highlightCommentUuid?: string;
  goBack?: boolean;
  noPadding?: boolean;
  showTimeReport?: boolean;
  hideTitle?: boolean;
  hideComments?: boolean;
  onRequestLoaded?: () => void;
  hideGoToProject?: boolean;
}

const RequestUpdate: FC<RequestUpdateProps> = ({
  uuid,
  projectUuid,
  pageUrl,
  wrapSize,
  highlightCommentUuid,
  goBack = false,
  noPadding = false,
  hideGoToProject,
  showTimeReport,
  hideTitle,
  hideComments,
  onRequestLoaded,
}) => {
  const [fetching, setFetching] = useState(true);
  const [request, setRequest] = useState<any>();
  const [formBuilder, setFormBuilder] = useState<FormBuilderData>();
  const [resetForm, setResetForm] = useState<RequestFormProps['resetForm']>();
  const [isSyncingFromWebSocket, setIsSyncingFromWebSocket] = useState(false);
  const [overBudgetPendingValues, setOverBudgetPendingValues] = useState<any>(null);
  const [estimatedResetTrigger, setEstimatedResetTrigger] = useState(0);

  const isDraft = request?.status === 'draft';

  const saveRequestImpl = async (values: any) => {
    values.ref_token = localRandomUuid;

    const response = await apiRequests.put(`${apiRoutes.REQUESTS}/${uuid}?with=project_time,uploads`, values);

    const data = parseData(response.data.data);

    if (values.status === 'new' && request?.status === 'draft') {
      message.success('Request submitted');
    }

    setFormBuilder(parseFormBuilder(data));

    setRequest((oldData: any) => ({
      ...data,
      tasks: oldData.tasks,
    }));

    return response;
  };

  const saveRequest = async (values: any) => {
    if (isSyncingFromWebSocket) {
      setIsSyncingFromWebSocket(false);
      return;
    }

    if (isEstimatedMaxOverBudget(values, request)) {
      setOverBudgetPendingValues(values);
      return;
    }

    return saveRequestImpl(values);
  };

  const fetchData = useCallback(async () => {
    try {
      const data = await fetchRequest(uuid, {
        with: 'total_timelogs,project_time,uploads',
      });

      setFormBuilder(parseFormBuilder(data));

      setRequest(data);
    } catch (error) {
      asyncErrorHandler(error);
    }
  }, [uuid]);

  useEffect(() => {
    setFetching(true);

    let timer: NodeJS.Timeout;

    fetchData()
      .then(() => {
        timer = setTimeout(() => {
          onRequestLoaded?.();
        }, 100);
      })
      .finally(() => setFetching(false));

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.projects.request',
      callback: (event: any) => {
        if (
          event.action === 'update' &&
          event.id === request?.uuid &&
          event.payload.ref_token !== localRandomUuid &&
          formBuilder
        ) {
          const oldRequest = cloneDeep(request);
          const oldFormBuilder = cloneDeep(formBuilder);

          fetchData()
            .then(() => {
              setIsSyncingFromWebSocket(true);
              setResetForm({ oldRequest, oldFormBuilder });
              setTimeout(() => setIsSyncingFromWebSocket(false), 100);
            })
            .catch(() => {});
        }
      },
    },
  });

  if (!fetching && !request) {
    return (
      <Card
        bordered={false}
        bodyStyle={{ padding: noPadding ? '0 0 8px' : undefined, paddingBottom: !noPadding ? 8 : undefined }}
      >
        <Result status="404" subTitle="Sorry, the page you visited does not exist." title="404" />
      </Card>
    );
  }

  if (fetching) {
    return (
      <Card
        bordered={false}
        bodyStyle={{ padding: noPadding ? '0 0 8px' : undefined, paddingBottom: !noPadding ? 8 : undefined }}
      >
        <Skeleton />
      </Card>
    );
  }

  const clientCheckoutUrl = `${process.env.REACT_APP_CLIENT_URL}/checkout`;
  const newEstimatedMax = overBudgetPendingValues?.estimated_max || 0;
  const currentEstimatedMax = request?.estimated_max || 0;
  const timeSpent = request?.time_spent || 0;
  const available = request?.project?.time_report?.available ?? 0;
  const allocatedTime = Math.max(currentEstimatedMax, timeSpent);
  const overage = Math.max(0, newEstimatedMax - allocatedTime - available);
  const estimatedTimeInHours = convertMinsToHrsMins(overage);

  const handleCopyUrl = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    navigator.clipboard.writeText(clientCheckoutUrl).then(() => {
      message.success({
        content: 'Link copied!',
        icon: <CheckCircleFilled style={{ color: '#52c41a' }} />,
        duration: 2,
      });
    });
  };

  return (
    <>
      <Modal
        visible={overBudgetPendingValues !== null}
        title={<span><FireTwoTone twoToneColor="#CC0000" style={{ marginRight: '8px' }} />Over Budget</span>}
        className="ant-modal-overbudget"
        okText="Yes, I have written approval"
        cancelText="No, I'll reach out to the client"
        onOk={() => {
          const pending = overBudgetPendingValues;
          setOverBudgetPendingValues(null);
          saveRequestImpl(pending);
        }}
        onCancel={() => {
          setOverBudgetPendingValues(null);
          fetchData();
          setEstimatedResetTrigger(prev => prev + 1);
        }}
        destroyOnClose
      >
        This entry will put <strong>{request?.project?.customer?.business_name}</strong> {estimatedTimeInHours}h over budget. Do you have written approval to continue? If not and this is urgent, ask them to purchase the amount of hours needed here:{' '}
        <a href={clientCheckoutUrl} onClick={handleCopyUrl} rel="noopener noreferrer">
          {clientCheckoutUrl}
        </a>{' '}
        to get things moving right away.
      </Modal>

      {isDraft ? (
        <RequestUpdateDraft
          request={request}
          formBuilder={formBuilder}
          projectUuid={projectUuid}
          pageUrl={pageUrl}
          resetForm={resetForm}
          noPadding={noPadding}
          goBack={goBack}
          estimatedResetTrigger={estimatedResetTrigger}
          onSaveRequest={saveRequest}
        />
      ) : (
        <RequestUpdateNoDraft
          request={request}
          formBuilder={formBuilder}
          projectUuid={projectUuid}
          hideGoToProject={hideGoToProject}
          pageUrl={pageUrl}
          wrapSize={wrapSize}
          noPadding={noPadding}
          highlightCommentUuid={highlightCommentUuid}
          resetForm={resetForm}
          goBack={goBack}
          showTimeReport={showTimeReport}
          hideComments={hideComments}
          estimatedResetTrigger={estimatedResetTrigger}
          setRequest={setRequest}
          onFetchRequest={fetchData}
          hideTitle={hideTitle}
          onSaveRequest={saveRequest}
        />
      )}
    </>
  );
};

export default RequestUpdate;
